/*
  ZEYRIVA – small storefront enhancements on top of Horizon.
  - <zeyriva-countdown>: live countdown inside an announcement (daily cut-off or fixed end date)
  - <zeyriva-popup>: newsletter popup, shown once per N days
*/

const pad = (n) => String(n).padStart(2, '0');

/** Milliseconds until the next HH:00 in the given IANA time zone. */
function msUntilDailyCutoff(hour, timeZone) {
  const now = new Date();
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hourCycle: 'h23',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(now)
      .map((p) => [p.type, p.value])
  );
  const secondsNow = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second);
  let diff = hour * 3600 - secondsNow;
  if (diff <= 0) diff += 24 * 3600;
  return diff * 1000;
}

class ZeyrivaCountdown extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode;
    this.hour = Number(this.dataset.hour || 0);
    this.end = this.dataset.end ? Date.parse(this.dataset.end) : NaN;
    this.timeZone = this.dataset.timeZone || 'Europe/Berlin';
    this.labels = {
      h: this.dataset.labelHours || 'h',
      m: this.dataset.labelMinutes || 'm',
      s: this.dataset.labelSeconds || 's',
    };
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  remaining() {
    if (this.mode === 'daily') return msUntilDailyCutoff(this.hour, this.timeZone);
    if (this.mode === 'date' && !Number.isNaN(this.end)) return this.end - Date.now();
    return -1;
  }

  tick() {
    const ms = this.remaining();
    if (ms <= 0) {
      this.hidden = true;
      clearInterval(this.timer);
      return;
    }
    const total = Math.floor(ms / 1000);
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600) + d * 24;
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    this.textContent = `${pad(h)} ${this.labels.h} ${pad(m)} ${this.labels.m} ${pad(s)} ${this.labels.s}`;
  }
}

if (!customElements.get('zeyriva-countdown')) customElements.define('zeyriva-countdown', ZeyrivaCountdown);

class ZeyrivaPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.storageKey = `zeyriva-popup-${this.dataset.sectionId}`;
    this.delay = Number(this.dataset.delay || 6) * 1000;
    this.frequencyDays = Number(this.dataset.frequency || 7);

    this.querySelectorAll('[data-popup-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', () => this.remember());

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.detail.sectionId === this.dataset.sectionId) this.open();
      });
      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.detail.sectionId === this.dataset.sectionId) this.dialog.close();
      });
      return;
    }

    // After a successful signup Shopify reloads with ?customer_posted=true – show the thank-you state.
    if (this.dataset.posted === 'true') {
      this.open();
      this.remember(365);
      return;
    }

    if (this.seenRecently()) return;
    this.timer = setTimeout(() => this.open(), this.delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  seenRecently() {
    try {
      const until = Number(localStorage.getItem(this.storageKey) || 0);
      return Date.now() < until;
    } catch (e) {
      return false;
    }
  }

  remember(days = this.frequencyDays) {
    try {
      localStorage.setItem(this.storageKey, String(Date.now() + days * 86400000));
    } catch (e) {
      /* storage unavailable – popup may show again, which is acceptable */
    }
  }

  open() {
    if (this.dialog.open) return;
    // Never interrupt another open dialog (menu, cart, search) – try again a little later.
    if (document.querySelector('dialog[open]')) {
      this.timer = setTimeout(() => this.open(), 4000);
      return;
    }
    this.dialog.showModal();
    this.classList.add('is-open');
  }

  close() {
    this.remember();
    this.classList.remove('is-open');
    this.dialog.close();
  }
}

if (!customElements.get('zeyriva-popup')) customElements.define('zeyriva-popup', ZeyrivaPopup);

/* Scroll reveal: sections that start below the fold rise in once when they enter the viewport. */
function initReveal() {
  if (window.Shopify && window.Shopify.designMode) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const sections = document.querySelectorAll('main > .shopify-section:not(:first-child)');
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  sections.forEach((section) => {
    if (section.getBoundingClientRect().top < window.innerHeight) return;
    section.classList.add('zeyriva-reveal');
    observer.observe(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initReveal, { once: true });
} else {
  initReveal();
}

/* Editorial menu: slide-in panel with sub-levels and image tiles (snippets/zeyriva-menu.liquid). */
class ZeyrivaMenu extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.opener = this.querySelector('[data-zm-open]');

    this.opener?.addEventListener('click', () => this.open());
    this.querySelector('[data-zm-search]')?.addEventListener('click', () => {
      document.querySelector('search-button button')?.click();
    });
    this.querySelectorAll('[data-zm-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });
    this.querySelectorAll('[data-zm-target]').forEach((b) =>
      b.addEventListener('click', () => this.showLevel(b.dataset.zmTarget))
    );
    this.querySelectorAll('[data-zm-back]').forEach((b) => b.addEventListener('click', () => this.showLevel('root')));
  }

  open() {
    if (this.dialog.open) return;
    this.showLevel('root', true);
    this.dialog.showModal();
    this.opener?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('zm-locked');
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
  }

  close() {
    if (!this.dialog.open || this.closing) return;
    this.closing = true;
    this.dialog.classList.remove('is-open');
    this.dialog.classList.add('is-closing');
    const done = () => {
      this.dialog.classList.remove('is-closing');
      this.dialog.close();
      document.documentElement.classList.remove('zm-locked');
      this.opener?.setAttribute('aria-expanded', 'false');
      this.opener?.focus();
      this.closing = false;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, 520);
  }

  showLevel(id, instant = false) {
    this.querySelectorAll('[data-zm-level]').forEach((level) => {
      const match = id === 'root' ? level.dataset.zmLevel === 'root' : level.id === id;
      level.classList.toggle('is-active', match);
      level.classList.toggle('is-instant', instant);
      if (match) {
        level.hidden = false;
      } else if (level.dataset.zmLevel !== 'root') {
        level.hidden = true;
      }
    });
    const active = this.querySelector('[data-zm-level].is-active');
    if (!instant) active?.querySelector('a, button')?.focus({ preventScroll: true });
  }
}

if (!customElements.get('zeyriva-menu')) customElements.define('zeyriva-menu', ZeyrivaMenu);

/* Floating "Filter & Sortieren" button: opens Horizon's filter drawer, hides near the footer. */
function initFilterPill() {
  const pill = document.querySelector('[data-zeyriva-filters]');
  if (!pill) return;
  const toggle = document.querySelector('.facets-toggle__button');
  if (!toggle) return;
  pill.hidden = false;
  pill.addEventListener('click', () => toggle.click());

  const footer = document.querySelector('footer, .shopify-section-group-footer-group');
  if (footer && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => pill.classList.toggle('is-hidden', entry.isIntersecting)).observe(footer);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFilterPill, { once: true });
} else {
  initFilterPill();
}

/* One-shot reveal for oversized wordmarks (footer) and similar elements. */
function initLetterReveal() {
  const targets = document.querySelectorAll('[data-zeyriva-reveal-letters]');
  if (!targets.length) return;
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    targets.forEach((t) => t.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.25 }
  );
  targets.forEach((t) => io.observe(t));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLetterReveal, { once: true });
} else {
  initLetterReveal();
}

/* Size-guide side panel (snippets/zeyriva-size-guide.liquid). Delegated, so it keeps working
   after Horizon re-renders the variant picker on variant change. */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-zsg-open]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.zsgOpen);
    if (dialog && !dialog.open) {
      dialog.showModal();
      requestAnimationFrame(() => dialog.classList.add('is-open'));
    }
    return;
  }
  const closer = event.target.closest('[data-zsg-close]');
  const dialog = closer ? closer.closest('dialog.zsg') : event.target.matches?.('dialog.zsg') ? event.target : null;
  if (dialog) {
    dialog.classList.remove('is-open');
    setTimeout(() => dialog.close(), window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 420);
  }
});

/* Withdrawal function (sections/zeyriva-withdrawal.liquid): two steps – details, then "Widerruf bestätigen". */
class ZeyrivaWithdrawal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('a[href$="#widerruf"], [data-zwd-open]');
      if (!trigger) return;
      event.preventDefault();
      this.open();
    });

    this.querySelectorAll('[data-zwd-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });

    this.querySelector('[data-zwd-next]')?.addEventListener('click', () => this.next());
    this.querySelector('[data-zwd-back]')?.addEventListener('click', () => this.step(1));

    // Coming from the order confirmation email: ?bestellung=#1001#widerruf pre-fills the order number.
    const order = new URLSearchParams(window.location.search).get('bestellung');
    const orderField = this.querySelector('[name="contact[Bestellnummer]"]');
    if (order && orderField && !orderField.value) orderField.value = order;

    if (this.dataset.posted === 'true' || window.location.hash === '#widerruf') this.open();
  }

  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
  }

  close() {
    this.dialog.classList.remove('is-open');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTimeout(() => this.dialog.close(), reduce ? 0 : 420);
    if (window.location.hash === '#widerruf') history.replaceState(null, '', window.location.pathname + window.location.search);
  }

  step(n) {
    this.querySelectorAll('[data-zwd-step]').forEach((s) => (s.hidden = s.dataset.zwdStep !== String(n)));
    this.querySelector(`[data-zwd-step="${n}"] input, [data-zwd-step="${n}"] button`)?.focus();
  }

  next() {
    const first = this.querySelector('[data-zwd-step="1"]');
    const fields = [...first.querySelectorAll('input, textarea')];
    for (const field of fields) {
      field.closest('.zwd__field')?.classList.toggle('is-invalid', !field.checkValidity());
      if (!field.checkValidity()) {
        field.reportValidity();
        return;
      }
    }

    const summary = this.querySelector('[data-zwd-summary]');
    const lines = [];
    summary.replaceChildren();
    fields.forEach((field) => {
      const label = field.closest('.zwd__field')?.querySelector('span')?.textContent.replace(/\s*\*$/, '').replace(/\s*\(.*\)$/, '') || field.name;
      const value = field.value.trim() || (field.name.includes('Artikel') ? 'Gesamte Bestellung' : '–');
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = label;
      dd.textContent = value;
      summary.append(dt, dd);
      lines.push(`${label}: ${value}`);
    });

    const body = this.querySelector('[data-zwd-body]');
    if (body) {
      body.value =
        'Hiermit widerrufe ich den von mir abgeschlossenen Vertrag über den Kauf der folgenden Waren.\n\n' +
        lines.join('\n') +
        `\n\nAbgesendet am: ${new Date().toLocaleString('de-DE')}`;
    }
    this.step(2);
  }
}

if (!customElements.get('zeyriva-withdrawal')) customElements.define('zeyriva-withdrawal', ZeyrivaWithdrawal);
